import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "../lib/utils"
import { Button } from "./button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

export interface SearchableSelectOption {
  value: string
  label: string | React.ReactNode
  /** Compact text shown in the closed trigger (falls back to string label / searchText) */
  triggerLabel?: string
  searchText?: string
  disabled?: boolean
}

interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value?: string
  onValueChange: (value: string) => void
  placeholder?: string
  emptyText?: string
  searchPlaceholder?: string
  /** Shown on the closed control when the selected value is not in the current options. */
  selectedLabel?: string
  className?: string
  disabled?: boolean
  loading?: boolean
  icon?: React.ReactNode
  searchable?: boolean
  onSearchChange?: (value: string) => void
}

export function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder = "Select option...",
  emptyText = "No options found.",
  searchPlaceholder = "Search...",
  selectedLabel,
  className,
  disabled = false,
  loading = false,
  searchable = true,
  icon,
  onSearchChange,
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const selectedOption = options.find((option) => option.value === value);

  const triggerDisplay = React.useMemo(() => {
    if (loading && !value) return "Loading..."
    if (!selectedOption) return selectedLabel || placeholder
    if (selectedOption.triggerLabel) return selectedOption.triggerLabel
    if (typeof selectedOption.label === "string") return selectedOption.label
    return selectedOption.searchText || placeholder
  }, [loading, value, selectedOption, placeholder, selectedLabel])

  // Filter options based on search input
  const filteredOptions = React.useMemo(() => {
    // If onSearchChange is provided, disable frontend filtering (backend search only)
    if (onSearchChange) return options;
    
    if (!searchable || !search.trim()) return options;
    const lower = search.trim().toLowerCase();
  
    return options.filter(
      (option) => {
        const labelText = typeof option.label === 'string' ? option.label : option.searchText || '';
        return labelText.toLowerCase().includes(lower);
      }
    );
  }, [options, search, searchable, onSearchChange]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
    "justify-between font-normal w-full", // 👈 added here
    !value && "text-muted-foreground",
    className
  )}
          disabled={disabled || (loading && !value)}
        >
          <div className="flex items-center min-w-0 flex-1 overflow-hidden">
            {icon && <span className="mr-2 flex-shrink-0">{icon}</span>}
            <span className="truncate text-left">{triggerDisplay}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)]" align="start">
        <Command shouldFilter={!onSearchChange}>
          {searchable && (
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={(value) => {
                setSearch(value)
                onSearchChange?.(value)
              }}
            />
          )}
          <CommandList className="max-h-60 overflow-y-auto">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={typeof option.label === 'string' ? option.label : option.searchText || ''}
                  disabled={option.disabled}
                  onSelect={() => {
                    onValueChange(option.value);
                    setOpen(false);
                    setSearch("");
                  }}
                  className="items-start py-2"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 mt-0.5 shrink-0",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="min-w-0 flex-1 text-left">{option.label}</div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
